import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { BaseButton, BaseCheckbox, BaseRadio } from "src/components";
import { BaseInputNumberFormat } from "src/components/BaseInputNumberFormat";
import { SpaceTypeT } from "src/pages/product/product-types";
import { useModal } from "src/recoil/modalState/hook";
import { commaizePrice, deleteCommas } from "src/utils";
import { EditObj } from "./SpaceUsageLimitModal";
import { decommaizeNumber } from "@toss/utils";
import { convertMinutesToDHMS } from "../libs";
import useDirtyState from "src/recoil/dirty/hooks";
import _ from "lodash";

const LimitFrom = ({
  spaceType,
  editObj,
  setEditObj,
  onClose,
  handleSubmit,
  isEditable,
  isDefault,
  isFeeAvailable,
}: {
  spaceType: SpaceTypeT;
  editObj: EditObj;
  setEditObj: Dispatch<SetStateAction<EditObj>>;
  onClose: () => void;
  handleSubmit: () => void;
  isEditable?: boolean;
  isDefault: boolean;
  isFeeAvailable?: boolean;
}) => {
  const [errors, setErrors] = useState({
    isUnLimitReservation: "",
    isMonthlyUnLimit: "",
    monthlyTime: "",
    isDailyUnLimit: "",
    dailyTime: "",
    monthlyFee: "",
    dailyFee: "",
  });

  const { setIsDirtyState, isDirtyState } = useDirtyState();
  const { setAbstractModalZ1 } = useModal();

  const [original, setOriginal] = useState(editObj);

  useEffect(() => {
    if (!_.isEqual(original, editObj)) {
      return setIsDirtyState(true);
    } else {
      setIsDirtyState(false);
    }
  }, [setIsDirtyState, editObj, original]);

  const { setBaseModal } = useModal();

  const renderTimeText = (isEnabled: boolean, time: string) => {
    if (isEnabled) {
      return time === "" || time === "0" ? "분 (0시간 0분)" : `분 (${convertMinutesToDHMS(deleteCommas(time))})`;
    }
    return;
  };

  const onValid = () => {
    let newErrors = {
      isUnLimitReservation: "",
      isMonthlyUnLimit: "",
      monthlyTime: "",
      isDailyUnLimit: "",
      dailyTime: "",
      monthlyFee: "",
      dailyFee: "",
    };

    if (!editObj.isUnLimitReservation) {
      if (!editObj.isTimeEnabled && !editObj.isFeeEnabled) {
        return setBaseModal({
          isOpen: true,
          btnRightTitle: "확인",
          title: "시간 또는 요금 이용한도를 설정해주세요",
        });
      }

      // 시간 validation
      if (editObj.isTimeEnabled) {
        if (!editObj.isMonthlyUnLimit && !editObj.isDailyUnLimit) {
          return setBaseModal({
            isOpen: true,
            btnRightTitle: "확인",
            title: "제한 설정 시 이용한도 입력이 필요합니다.",
          });
        }

        if (editObj.isMonthlyUnLimit) {
          if (Number(editObj.monthlyTime) < 0 || editObj.monthlyTime === "") {
            newErrors.monthlyTime = "이용한도를 입력해주세요.";
          } else if ((decommaizeNumber(editObj.monthlyTime.toString()) % 60) % 30 !== 0) {
            newErrors.monthlyTime = "30분 단위로 입력해주세요";
          }
        }

        if (editObj.isDailyUnLimit) {
          if (Number(editObj.dailyTime) < 0 || editObj.dailyTime === "") {
            newErrors.dailyTime = "이용한도를 입력해주세요.";
          } else if ((decommaizeNumber(editObj.dailyTime.toString()) % 60) % 30 !== 0) {
            newErrors.dailyTime = "30분 단위로 입력해주세요";
          } else if (
            editObj.isMonthlyUnLimit &&
            editObj.monthlyTime !== "" &&
            editObj.dailyTime !== "" &&
            decommaizeNumber(editObj.monthlyTime.toString()) < decommaizeNumber(editObj.dailyTime.toString())
          ) {
            newErrors.dailyTime = "월 이용 한도 내에서 입력해주세요.";
          }
        }
      }

      // 요금 validation
      if (editObj.isFeeEnabled) {
        if (!editObj.isMonthlyFeeEnabled && !editObj.isDailyFeeEnabled) {
          return setBaseModal({
            isOpen: true,
            btnRightTitle: "확인",
            title: "제한 설정 시 이용한도 입력이 필요합니다.",
          });
        }

        if (editObj.isMonthlyFeeEnabled) {
          if (editObj.monthlyFee === "" || Number(editObj.monthlyFee) < 0) {
            newErrors.monthlyFee = "이용한도를 입력해주세요.";
          }
        }

        if (editObj.isDailyFeeEnabled) {
          if (editObj.dailyFee === "" || Number(editObj.dailyFee) < 0) {
            newErrors.dailyFee = "이용한도를 입력해주세요.";
          } else if (
            editObj.isMonthlyFeeEnabled &&
            editObj.monthlyFee !== "" &&
            editObj.dailyFee !== "" &&
            decommaizeNumber(editObj.monthlyFee.toString()) < decommaizeNumber(editObj.dailyFee.toString())
          ) {
            newErrors.dailyFee = "월 이용 한도 내에서 입력해주세요.";
          }
        }
      }

      if (newErrors.monthlyTime || newErrors.dailyTime || newErrors.monthlyFee || newErrors.dailyFee) {
        return setErrors(newErrors);
      }
    }

    setErrors(newErrors);

    setBaseModal({
      isOpen: true,
      btnRightTitle: "확인",
      btnLeftTitle: "취소",
      title: "저장하시겠습니까?",
      children: "저장 후에는 변경 사항이 즉시 적용됩니다.",
      onClick: () => {
        handleSubmit();
        setBaseModal({ isOpen: false });
        setIsDirtyState(false);
      },
    });
  };

  const onCloseTabModal = () => {
    if (isDirtyState) {
      setBaseModal({
        isOpen: true,
        title: "취소하시겠습니까?",
        children: "저장하지 않고 취소하면 입력한 내용이 모두 삭제됩니다.",
        btnLeftTitle: "취소",
        btnRightTitle: "확인",
        onClick: () => {
          setIsDirtyState(false);
          setBaseModal({ isOpen: false });
          setAbstractModalZ1({ isOpen: false });
        },
        onClose: () => {
          setBaseModal({ isOpen: false });
        },
      });
    } else {
      setIsDirtyState(false);
      setAbstractModalZ1({ isOpen: false });
    }
  };

  const isLimited = !editObj.isUnLimitReservation;
  const feeDisabled = !isFeeAvailable;

  // 기본 정책: 시간/요금 동시 선택 가능 (체크박스)
  // 개별 정책: 시간/요금 택 1 (라디오 버튼)
  const handleTimeToggle = (value: boolean) => {
    if (isDefault) {
      // 체크박스: 독립적으로 토글, 적용 체크박스는 해제 시 미선택
      setEditObj((prev) => ({
        ...prev,
        isTimeEnabled: value,
        ...(!value && { isMonthlyUnLimit: false, isDailyUnLimit: false }),
      }));
    } else {
      // 라디오: 시간 선택 시 요금 해제
      setEditObj((prev) => ({
        ...prev,
        isTimeEnabled: true,
        isMonthlyUnLimit: false,
        isDailyUnLimit: false,
        isFeeEnabled: false,
        isMonthlyFeeEnabled: false,
        isDailyFeeEnabled: false,
      }));
    }
  };

  const handleFeeToggle = (value: boolean) => {
    if (feeDisabled) return;
    if (isDefault) {
      // 체크박스: 독립적으로 토글, 적용 체크박스는 해제 시 미선택
      setEditObj((prev) => ({
        ...prev,
        isFeeEnabled: value,
        ...(!value && { isMonthlyFeeEnabled: false, isDailyFeeEnabled: false }),
      }));
    } else {
      // 라디오: 요금 선택 시 시간 해제
      setEditObj((prev) => ({
        ...prev,
        isFeeEnabled: true,
        isMonthlyFeeEnabled: false,
        isDailyFeeEnabled: false,
        isTimeEnabled: false,
        isMonthlyUnLimit: false,
        isDailyUnLimit: false,
      }));
    }
  };

  return (
    <>
      <section className="base-abstract-modal__contents px30">
        <div className="pt10">
          <ul className="base-list">
            <li className="font14">기본 정책 설정: 전체 공용공간의 예약 사용량을 제한하는 목적으로 이용한도를 설정할 수 있습니다.</li>
            <li className="font14">개별 정책 설정: 각 공간별로 예약 사용량을 제한하는 목적으로 이용한도를 설정할 수 있습니다.</li>
          </ul>
        </div>

        {/* 이용한도 제한 */}
        <div className="limit-form__row">
          <div className="limit-form__label">
            <span>이용한도 제한</span>
          </div>
          <div className="limit-form__radio-group">
            <BaseRadio
              id={spaceType + "_isLimited_True"}
              name={spaceType + "_isLimited"}
              checked={editObj.isUnLimitReservation === true}
              label="무제한"
              onChange={() => {
                setEditObj((prev) => ({
                  ...prev,
                  isUnLimitReservation: true,
                  isTimeEnabled: false,
                  isMonthlyUnLimit: false,
                  isDailyUnLimit: false,
                  isFeeEnabled: false,
                  isMonthlyFeeEnabled: false,
                  isDailyFeeEnabled: false,
                }));
              }}
              className="mr10"
            />
            <BaseRadio
              id={spaceType + "_isLimited_False"}
              name={spaceType + "_isLimited"}
              checked={!editObj.isUnLimitReservation}
              label="제한"
              onChange={() => {
                if (isDefault) {
                  // 기본 정책: 시간/요금 체크 가능 상태, 적용 체크박스는 미선택
                  setEditObj((prev) => ({
                    ...prev,
                    isUnLimitReservation: false,
                    isTimeEnabled: true,
                    isMonthlyUnLimit: false,
                    isDailyUnLimit: false,
                    isFeeEnabled: !feeDisabled,
                    isMonthlyFeeEnabled: false,
                    isDailyFeeEnabled: false,
                  }));
                } else {
                  // 개별 정책: 시간만 기본 선택 (택 1), 적용 체크박스는 미선택
                  setEditObj((prev) => ({
                    ...prev,
                    isUnLimitReservation: false,
                    isTimeEnabled: true,
                    isMonthlyUnLimit: false,
                    isDailyUnLimit: false,
                    isFeeEnabled: false,
                    isMonthlyFeeEnabled: false,
                    isDailyFeeEnabled: false,
                  }));
                }
              }}
            />
          </div>
        </div>

        {/* 제한 이용한도 설정 */}
        <div className="limit-form__row">
          <div className="limit-form__label">
            <span className="limit-form__required">*</span>
            <span>제한 이용한도 설정</span>
          </div>
          <div className="limit-form__content">
            {/* 시간 선택 */}
            <div className="limit-form__control-row">
              {isDefault ? (
                <BaseCheckbox
                  className="w-fit"
                  id={spaceType + "_timeEnabled"}
                  name={spaceType + "_timeEnabled"}
                  checked={editObj.isTimeEnabled}
                  disabled={!isLimited}
                  onChange={handleTimeToggle}
                  label="시간"
                />
              ) : (
                <BaseRadio
                  id={spaceType + "_timeEnabled"}
                  name={spaceType + "_limitType"}
                  checked={editObj.isTimeEnabled}
                  disabled={!isLimited}
                  onChange={() => handleTimeToggle(true)}
                  label="시간"
                />
              )}
            </div>

            {/* 시간 그레이 박스 */}
            <div className="limit-form__gray-box">
              {/* 월 시간 이용한도 */}
              <div>
                <div className="limit-form__control-row">
                  <div className="limit-form__label">
                    <span>월 시간 이용한도</span>
                  </div>
                  <div className="limit-form__input-group">
                    <BaseCheckbox
                      className="w-fit"
                      id={spaceType + "_monthlyTime"}
                      name={spaceType + "_monthlyTime"}
                      checked={editObj.isMonthlyUnLimit}
                      disabled={!isLimited || !editObj.isTimeEnabled}
                      onChange={(value) => {
                        setEditObj((prev) => ({ ...prev, isMonthlyUnLimit: value }));
                      }}
                      label="적용"
                    />
                    <div className="limit-form__input-wrap">
                      <BaseInputNumberFormat
                        maxLength={7}
                        type="text"
                        value={commaizePrice(editObj.monthlyTime)}
                        onChange={(text: string) => {
                          setEditObj((prev) => ({ ...prev, monthlyTime: commaizePrice(text) }));
                        }}
                        disable={!isLimited || !editObj.isTimeEnabled || !editObj.isMonthlyUnLimit}
                      />
                    </div>
                    <span className="font14">
                      {renderTimeText(isLimited && editObj.isTimeEnabled, editObj.monthlyTime.toString()) ?? "분 (0시간 0분)"}
                    </span>
                  </div>
                </div>
                {errors.monthlyTime && <p className="validation-text">{errors.monthlyTime}</p>}
              </div>

              {/* 일 시간 이용한도 */}
              <div>
                <div className="limit-form__control-row">
                  <div className="limit-form__label">
                    <span>일 시간 이용한도</span>
                  </div>
                  <div className="limit-form__input-group">
                    <BaseCheckbox
                      className="w-fit"
                      id={spaceType + "_dailyTime"}
                      name={spaceType + "_dailyTime"}
                      checked={editObj.isDailyUnLimit}
                      disabled={!isLimited || !editObj.isTimeEnabled}
                      onChange={(value) => {
                        setEditObj((prev) => ({ ...prev, isDailyUnLimit: value }));
                      }}
                      label="적용"
                    />
                    <div className="limit-form__input-wrap">
                      <BaseInputNumberFormat
                        maxLength={7}
                        type="text"
                        value={commaizePrice(editObj.dailyTime)}
                        onChange={(text: string) => {
                          setEditObj((prev) => ({ ...prev, dailyTime: commaizePrice(text) }));
                        }}
                        disable={!isLimited || !editObj.isTimeEnabled || !editObj.isDailyUnLimit}
                      />
                    </div>
                    <span className="font14">
                      {renderTimeText(isLimited && editObj.isTimeEnabled, editObj.dailyTime.toString()) ?? "분 (0시간 0분)"}
                    </span>
                  </div>
                </div>
                {errors.dailyTime && <p className="validation-text">{errors.dailyTime}</p>}
              </div>
            </div>

            {/* 요금 선택 */}
            <div className="limit-form__control-row">
              {isDefault ? (
                <BaseCheckbox
                  className="w-fit"
                  id={spaceType + "_feeEnabled"}
                  name={spaceType + "_feeEnabled"}
                  checked={editObj.isFeeEnabled}
                  disabled={!isLimited || feeDisabled}
                  onChange={handleFeeToggle}
                  label="요금"
                />
              ) : (
                <BaseRadio
                  id={spaceType + "_feeEnabled"}
                  name={spaceType + "_limitType"}
                  checked={editObj.isFeeEnabled}
                  disabled={!isLimited || feeDisabled}
                  onChange={() => handleFeeToggle(true)}
                  label="요금"
                />
              )}
            </div>

            {/* 요금 그레이 박스 */}
            <div className="limit-form__gray-box">
              {/* 월 요금 이용한도 */}
              <div>
                <div className="limit-form__control-row">
                  <div className="limit-form__label">
                    <span>월 요금 이용한도</span>
                  </div>
                  <div className="limit-form__input-group">
                    <BaseCheckbox
                      className="w-fit"
                      id={spaceType + "_monthlyFee"}
                      name={spaceType + "_monthlyFee"}
                      checked={editObj.isMonthlyFeeEnabled}
                      disabled={!isLimited || feeDisabled || !editObj.isFeeEnabled}
                      onChange={(value) => {
                        setEditObj((prev) => ({ ...prev, isMonthlyFeeEnabled: value }));
                      }}
                      label="적용"
                    />
                    <div className="limit-form__input-wrap">
                      <BaseInputNumberFormat
                        maxLength={13}
                        type="text"
                        value={commaizePrice(editObj.monthlyFee)}
                        onChange={(text: string) => {
                          setEditObj((prev) => ({ ...prev, monthlyFee: commaizePrice(text) }));
                        }}
                        disable={!isLimited || feeDisabled || !editObj.isFeeEnabled || !editObj.isMonthlyFeeEnabled}
                      />
                    </div>
                    <span className="font14">원</span>
                  </div>
                </div>
                {errors.monthlyFee && <p className="validation-text">{errors.monthlyFee}</p>}
              </div>

              {/* 일 요금 이용한도 */}
              <div>
                <div className="limit-form__control-row">
                  <div className="limit-form__label">
                    <span>일 요금 이용한도</span>
                  </div>
                  <div className="limit-form__input-group">
                    <BaseCheckbox
                      className="w-fit"
                      id={spaceType + "_dailyFee"}
                      name={spaceType + "_dailyFee"}
                      checked={editObj.isDailyFeeEnabled}
                      disabled={!isLimited || feeDisabled || !editObj.isFeeEnabled}
                      onChange={(value) => {
                        setEditObj((prev) => ({ ...prev, isDailyFeeEnabled: value }));
                      }}
                      label="적용"
                    />
                    <div className="limit-form__input-wrap">
                      <BaseInputNumberFormat
                        maxLength={13}
                        type="text"
                        value={commaizePrice(editObj.dailyFee)}
                        onChange={(text: string) => {
                          setEditObj((prev) => ({ ...prev, dailyFee: commaizePrice(text) }));
                        }}
                        disable={!isLimited || feeDisabled || !editObj.isFeeEnabled || !editObj.isDailyFeeEnabled}
                      />
                    </div>
                    <span className="font14">원</span>
                  </div>
                </div>
                {errors.dailyFee && <p className="validation-text">{errors.dailyFee}</p>}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="base-abstract-modal__btn-wrap">
        <BaseButton title={"취소"} className="color-white" onClick={onCloseTabModal} />
        {isEditable && <BaseButton title={"저장"} onClick={() => onValid()} />}
      </section>
    </>
  );
};

export default LimitFrom;
